import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, DateInput, FORM_LABEL_CLASS } from '@safetyhub/ui';
import {
  exemptionBasisDefinition,
  formatDateToLocal,
  parseDateLocalToUTC,
  type LegalObligationSubjectView,
  type LegalOrgObligationRow,
} from '@safetyhub/shared';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { apiErrorMessage } from '../../lib/api';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { describeMissingInstance, INSTANCE_STATUS_LABEL, subjectExcludedText } from '../../lib/legalDocumentLabels';
import {
  downloadOrgEvidenceFile,
  getOrgObligations,
  legalOrgObligationsQueryKeys,
  listOrgEvidences,
  materializeOrgInstance,
  uploadOrgEvidenceFile,
  uploadOrgExemption,
} from '../../lib/legalOrgObligations';
import { usePermissions } from '../../hooks/usePermissions';
import ExemptionModal, { type ExemptionSubmitInput } from '../../components/legal-documents/ExemptionModal';

/**
 * 「우리 기관의 법정 의무」(②-g3 G4 — 조직 층) — 기관 조직 «자기»의 의무(기관 종사자 직무교육 신규·보수 · 법 제32조①5 · 시행규칙 제29조①5·6)를
 * 기관 종사자마다 한 줄로 본다. 현장 목록(`SiteObligationsPage`)과 **분리된 화면**이다 — 현장 의무가 여기 섞이지 않고 이 의무도 현장 목록에 없다
 * (서버 술어 `OBLIGOR_OF_LAYER`). 열림 판정은 서버(`GET /legal-documents/organization/obligations` — 기관 분류 ∧ 플래그 · 아니면 404)이고 메뉴 입구는
 * visibility의 `organizationLayer`다(같은 술어).
 *
 * 이행 등록은 **외부 파일(이수증·수료증)**과 **면제 기록**(2026-11-05 결정 3 — 시행규칙 제30조 · 현장 목록과 같은 `ExemptionModal` · 선택지는 서버가
 * 대상 줄마다 싣는 `exemptionBases`)이다 — 조직 층에는 우리 양식 문서·사건 등록 입구가 없다(서버 서비스 docblock). 등록 순서: 그 사람의 지금 기간
 * 항목이 없으면 먼저 만든다(멱등) → 파일(또는 면제)을 올린다. 보수교육은 신규교육 이수일부터 기간이 선다(그 전에는 「선행 교육 이수 후」로 그린다).
 */
export default function OrganizationObligationsPage() {
  const companyId = useAuthStore((s) => s.currentMembership?.companyId);
  const { can } = usePermissions();
  const canManage = can('obligation.manage');
  // 면제는 별 권한(`obligation.exempt` — 허위가 가능해 감사 대상 · 현장 목록과 같은 키)
  const canExempt = can('obligation.exempt');
  const { data, isLoading, error } = useQuery({
    queryKey: legalOrgObligationsQueryKeys.obligations(companyId ?? ''),
    queryFn: () => getOrgObligations(companyId!),
    enabled: !!companyId,
    retry: false,
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-0">
      <div className="mb-6">
        <Link to="/legal-documents" className="text-sm font-medium text-ink-500 hover:text-ink-700">
          ← 법정 서류 관리
        </Link>
        <h1 className="mt-2 text-[26px] font-extrabold tracking-tight text-ink-900">우리 기관의 법정 의무</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">
          기관 종사자(위탁·지도업무를 수행하는 사람)의 직무교육입니다. 조직 역할에서 「기관 종사자」로 배정한 사람마다 기한이 따로 정해집니다.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error || !data ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">{apiErrorMessage(error) ?? '우리 기관의 법정 의무를 불러오지 못했습니다.'}</p>
        </div>
      ) : data.obligations.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
          <p className="text-sm font-medium text-ink-900">우리 기관에 걸린 법정 의무가 없습니다</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.obligations.map((row) => (
            <ObligationCard key={row.obligationId} companyId={companyId!} timezone={data.timezone} row={row} canManage={canManage} canExempt={canExempt} />
          ))}
        </div>
      )}
    </div>
  );
}

function ObligationCard({
  companyId,
  timezone,
  row,
  canManage,
  canExempt,
}: {
  companyId: string;
  timezone: string;
  row: LegalOrgObligationRow;
  canManage: boolean;
  canExempt: boolean;
}) {
  return (
    <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
      <div className="border-b border-ink-100 px-5 py-4">
        <h2 className="text-base font-bold text-ink-900">{row.title}</h2>
        {row.legalBasis && <p className="mt-0.5 text-xs text-ink-500">{row.legalBasis}</p>}
      </div>
      {row.subjects.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-500">
          {row.subjectNotice === 'ROLE_ASSIGNMENT_REQUIRED'
            ? '대상자가 없습니다. 조직 설정의 역할·선임에서 위탁·지도업무를 수행하는 사람을 「기관 종사자」로 배정하세요.'
            : '대상자가 없습니다.'}
        </p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {row.subjects.map((s) => (
            <SubjectLine
              key={s.subjectKey}
              companyId={companyId}
              timezone={timezone}
              obligationId={row.obligationId}
              obligationTitle={row.title}
              subject={s}
              canManage={canManage}
              canExempt={canExempt}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function SubjectLine({
  companyId,
  timezone,
  obligationId,
  obligationTitle,
  subject,
  canManage,
  canExempt,
}: {
  companyId: string;
  timezone: string;
  obligationId: string;
  obligationTitle: string;
  subject: LegalObligationSubjectView;
  canManage: boolean;
  canExempt: boolean;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [formOpen, setFormOpen] = useState(false);
  const [showEvidences, setShowEvidences] = useState(false);
  const [exemptionOpen, setExemptionOpen] = useState(false);
  const [exemptionError, setExemptionError] = useState<string | null>(null);
  const today = todayDateInputInTimezone(timezone);
  const [fulfilledAt, setFulfilledAt] = useState(today);
  const [file, setFile] = useState<File | null>(null);

  const instance = subject.currentInstance;
  const excluded = subject.excludedReason
    ? subjectExcludedText({ excludedReason: subject.excludedReason, pendingFieldLabels: subject.pendingFieldLabels })
    : null;
  const missing = instance ? null : describeMissingInstance(subject.currentPeriod);
  const status = instance ? INSTANCE_STATUS_LABEL[instance.status] : null;
  const deadline = instance?.deadline ?? missing?.deadline ?? null;
  const canRegister = canManage && !excluded && subject.currentPeriod.kind === 'period';
  // 면제 입구(결정 3) — 근거는 서버가 그 사람의 직위로 싣는다(`exemptionBases` · 비면 입구가 없다) · 이미 닫힌 기간에는 두지 않는다(현장 목록과 같은 규칙)
  const closed = instance?.status === 'FULFILLED' || instance?.status === 'FULFILLED_LATE';
  const canRecordExemption = canExempt && !excluded && subject.currentPeriod.kind === 'period' && !closed && (subject.exemptionBases?.length ?? 0) > 0;

  /** 그 사람의 지금 기간 항목 — 없으면 만든다(멱등 · 이수증·면제 둘 다 이 입구) */
  const ensureInstanceId = async () =>
    instance?.instanceId ?? (await materializeOrgInstance(companyId, obligationId, { subjectKey: subject.subjectKey })).instanceId;

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('파일을 선택하세요.');
      const instanceId = await ensureInstanceId();
      return uploadOrgEvidenceFile(companyId, instanceId, { file, fulfilledAt: parseDateLocalToUTC(fulfilledAt, timezone).toISOString() });
    },
    onSuccess: () => {
      showToast('이수증을 등록했습니다.', 'success');
      setFormOpen(false);
      setFile(null);
      void queryClient.invalidateQueries({ queryKey: legalOrgObligationsQueryKeys.obligations(companyId) });
    },
    onError: (err) => showToast(apiErrorMessage(err) ?? '이수증을 등록하지 못했습니다.', 'error'),
  });

  const exempt = useMutation({
    mutationFn: async (input: ExemptionSubmitInput) => {
      const instanceId = await ensureInstanceId();
      return uploadOrgExemption(companyId, instanceId, input);
    },
    onSuccess: () => {
      setExemptionOpen(false);
      showToast('면제를 기록했습니다. 이 사람의 그 기간이 닫혔습니다.', 'success');
      void queryClient.invalidateQueries({ queryKey: legalOrgObligationsQueryKeys.obligations(companyId) });
    },
    onError: (err) => setExemptionError(apiErrorMessage(err) ?? '면제 등록에 실패했습니다.'),
  });

  return (
    <li className="px-5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-900">{subject.label}</p>
          <p className="mt-0.5 text-xs text-ink-500">
            {excluded ?? (deadline ? `기한 ${formatDateToLocal(new Date(deadline))}` : missing?.hint ?? '')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status ? (
            <Badge tone={status.tone} size="sm" dot>
              {status.label}
            </Badge>
          ) : missing ? (
            <Badge tone="slate" size="sm">
              {missing.label}
            </Badge>
          ) : null}
          {instance && instance.evidenceCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowEvidences((v) => !v)}>
              이수증 {instance.evidenceCount}
            </Button>
          )}
          {canRegister && (
            <Button variant="secondary" size="sm" onClick={() => setFormOpen((v) => !v)}>
              이수증 등록
            </Button>
          )}
          {canRecordExemption && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExemptionError(null);
                setExemptionOpen(true);
              }}
            >
              면제 등록
            </Button>
          )}
        </div>
      </div>
      {exemptionOpen && (
        <ExemptionModal
          open
          bases={subject.exemptionBases ?? []}
          obligationTitle={obligationTitle}
          subjectLabel={subject.label}
          timezone={timezone}
          submitting={exempt.isPending}
          errorMessage={exemptionError}
          onSubmit={(input) => {
            setExemptionError(null);
            exempt.mutate(input);
          }}
          onClose={() => setExemptionOpen(false)}
        />
      )}
      {formOpen && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-[10px] border border-ink-100 bg-surface-subtle p-3">
          <DateInput id={`org-fulfilled-${subject.subjectKey}`} label="이수일" value={fulfilledAt} onChange={setFulfilledAt} max={today} disabled={upload.isPending} />
          <label className="flex flex-col">
            <span className={FORM_LABEL_CLASS}>이수증 파일</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              aria-label="이수증 파일"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={upload.isPending}
              className="text-sm"
            />
          </label>
          <Button size="sm" onClick={() => upload.mutate()} disabled={!file || !fulfilledAt || upload.isPending}>
            {upload.isPending ? '등록 중…' : '등록'}
          </Button>
        </div>
      )}
      {showEvidences && instance && <EvidenceList companyId={companyId} instanceId={instance.instanceId} />}
    </li>
  );
}

function EvidenceList({ companyId, instanceId }: { companyId: string; instanceId: string }) {
  const { showToast } = useToastStore();
  const { data, isLoading } = useQuery({
    queryKey: legalOrgObligationsQueryKeys.evidences(companyId, instanceId),
    queryFn: () => listOrgEvidences(companyId, instanceId),
  });
  const download = async (evidenceId: string, filename: string) => {
    try {
      const blob = await downloadOrgEvidenceFile(companyId, evidenceId);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 한 박자 뒤에 푼다(선례 `EvidenceListModal`)
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '파일을 내려받지 못했습니다.', 'error');
    }
  };
  if (isLoading) return <p className="mt-2 text-xs text-ink-500">불러오는 중…</p>;
  return (
    <ul className="mt-2 space-y-1">
      {(data?.evidences ?? []).map((e) => (
        <li key={e.evidenceId} className="flex items-center justify-between text-xs text-ink-700">
          <span>
            {e.exemptionBasis ? '면제 ' : '이수일 '}
            {formatDateToLocal(new Date(e.fulfilledAt))}
            {e.exemptionBasis ? ` · ${exemptionBasisDefinition(e.exemptionBasis)?.label ?? e.exemptionBasis}` : ''}
            {e.late ? ' · 기한 후' : ''}
            {e.attachedByName ? ` · ${e.attachedByName}` : ''}
          </span>
          {e.file && (
            <button type="button" className="font-medium text-brand-600 hover:underline" onClick={() => void download(e.evidenceId, e.file!.filename)}>
              {e.file.filename}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
